import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Message, Segment } from 'semantic-ui-react';
import { iRootState } from '../../store';
import { CustomDropDownOptions } from '../../types';
import { dangerNotification, successNotification } from '../../services/notification';
import { toAbsoluteUrl } from '../../utils/toAbsoluteUrl';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import Button from '../../components/Button';
import { exportStandaloneChargesReport, ListStandaloneChargesFiltersDTO } from '../../services/standalone-charges';
import { unmaskCpfOrCnpj } from '../../services/masks';

import {
  initialFilters,
  standaloneChargesLabel,
  statusLabelMap,
  statusOptions
} from './constants';
import { StandaloneChargesTab } from './types';
import { useStandaloneChargesList } from './hooks/useStandaloneChargesList';
import { useChargeActions } from './hooks/useChargeActions';
import { useStandaloneChargesActivation } from './hooks/useStandaloneChargesActivation';
import { useCreateStandaloneChargeForm } from './hooks/useCreateStandaloneChargeForm';
import CreateChargeModal from './components/CreateChargeModal';
import SuccessModal from './components/SuccessModal';
import CancelChargeModal from './components/CancelChargeModal';
import FiltersBar from './components/FiltersBar';
import ChargesTable from './components/ChargesTable';
import ChargesTabs from './components/ChargesTabs';
import ChargeDetailsModal from './components/ChargeDetailsModal';
import KycBanner from './components/KycBanner';
import RetryBiometryConfirmModal from './components/RetryBiometryConfirmModal';
import ActivationGate from './components/ActivationGate/ActivationGate';
import ActivationWizardModal from './components/ActivationGate/ActivationWizardModal';
import ActivationStepContent from './components/ActivationGate/ActivationStepContent';
import CommissioningChargesList from './components/CommissioningChargesList';
import EstatesChargesList from './components/EstatesChargesList';

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

type Props = ReturnType<typeof mapState>;

const StandaloneChargesPage = ({
  availableBrokersAndEstates,
  user
}: Props) => {
  const lastActivationStep = 5;
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [createdChargeLink, setCreatedChargeLink] = useState('');
  const [createdPaymentMethod, setCreatedPaymentMethod] = useState<'PIX' | 'BOLETO'>(
    'PIX'
  );
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsChargeId, setDetailsChargeId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<StandaloneChargesTab>('sent');
  const [exportLoading, setExportLoading] = useState(false);
  const role = user?.role as UserRolesEnum | undefined;
  const isEstateUser = (role || '').includes('ESTATE');
  const isBrokerAdmin = role === UserRolesEnum.brokerAdmin;
  const isEstateAdmin = role === UserRolesEnum.estateAdmin;
  const isAdminUser =
    role === UserRolesEnum.brokerAdmin || role === UserRolesEnum.brokerAnalyst;
  const canManageActivation = isBrokerAdmin || isEstateUser;
  const onboardingEstateId = useMemo(
    () => new URLSearchParams(window.location.search).get('estateId') || '',
    []
  );
  const needsActivationGate = isEstateUser || Boolean(onboardingEstateId);

  const estateOptions = useMemo(() => {
    return (availableBrokersAndEstates?.estates || []).map(
      (item: CustomDropDownOptions) => {
        const valueId = typeof item.value === 'string' ? item.value : item.value?.id;
        return {
          key: valueId || String(item.key),
          text: item.text || '',
          value: valueId || ''
        };
      }
    );
  }, [availableBrokersAndEstates?.estates]);

  const {
    isLoading,
    items,
    total,
    currentPage,
    setCurrentPage,
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    loadData,
    handleSearch,
    clearFilters
  } = useStandaloneChargesList({
    initialFilters,
    enabled: !needsActivationGate
  });

  const activationEstateId = useMemo(() => {
    if (onboardingEstateId) {
      return onboardingEstateId;
    }

    if (isEstateUser) {
      return (
        availableBrokersAndEstates?.activeEstateId ||
        user?.estate?.id ||
        ''
      );
    }

    return filters.estateId || appliedFilters.estateId || '';
  }, [
    appliedFilters.estateId,
    availableBrokersAndEstates?.activeEstateId,
    filters.estateId,
    isEstateUser,
    onboardingEstateId,
    user?.estate?.id
  ]);

  const enableActivationManagement =
    needsActivationGate || (canManageActivation && Boolean(activationEstateId));

  const activation = useStandaloneChargesActivation({
    needsActivationGate,
    enableActivationManagement,
    activationEstateId,
    appliedFilters,
    onEnabled: () => {
      loadData();
    }
  });

  const {
    activationStatus,
    activationStatusError,
    activationLoading,
    activationSaving,
    activationForm,
    activationWizardOpen,
    activationStep,
    activationStepError,
    addressLookupLoading,
    addressLookupError,
    addressAutoFilled,
    canCreateCharges,
    isFeatureEnabled,
    activationIsPendingKyc,
    activationIsBiometryRejected,
    activationIsSupportRequired,
    canRetryBiometry,
    activationBiometryMeta,
    activationKycLink,
    activationKycPageUrl,
    activationKycQrImageUrl,
    hasLocalDraft,
    showActivationAction,
    activationActionLabel,
    loadActivationStatus,
    updateActivationFormField,
    openActivationWizard,
    closeActivationWizard,
    handleNextActivationStep,
    handleBackActivationStep,
    handleZipCodeLookup,
    handleActivate,
    retryBiometryModalOpen,
    retryBiometrySaving,
    openRetryBiometryModal,
    closeRetryBiometryModal,
    handleRetryBiometry
  } = activation;

  useEffect(() => {
    if (needsActivationGate && canCreateCharges) {
      loadData(currentPage, appliedFilters);
    }
  }, [needsActivationGate, canCreateCharges, currentPage, appliedFilters, loadData]);

  useEffect(() => {
    if (!isAdminUser && activeTab === 'estates') {
      setActiveTab('sent');
    }
  }, [isAdminUser, activeTab]);

  const openEstateInSentTab = (estateId: string) => {
    const nextFilters = { ...filters, estateId };
    const params = new URLSearchParams(window.location.search);
    params.set('estateId', estateId);
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${params.toString()}`
    );
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setCurrentPage(1);
    setActiveTab('sent');
  };

  const {
    actionLoading,
    copyChargeLink,
    handleResendEmail,
    cancelModalOpen,
    chargeToCancel,
    openCancelModal,
    closeCancelModal,
    confirmCancelCharge,
    handleRenewCharge
  } = useChargeActions(
    () => loadData(),
    renewedCharge => {
      setCreatedChargeLink(renewedCharge.chargeLinkUrl);
      setCreatedPaymentMethod(renewedCharge.paymentMethod);
      setSuccessModalOpen(true);
    }
  );

  const needsOnboarding = !canCreateCharges || activationStatus?.activation == null;
  const isCreateBlockedByActivation = needsActivationGate && !canCreateCharges;
  const isCreateBlockedByPendingKyc = needsActivationGate && activationIsPendingKyc;
  const isCreateBlockedByBiometryIssue =
    needsActivationGate && (activationIsBiometryRejected || activationIsSupportRequired);
  const isCreateBlocked =
    isCreateBlockedByActivation ||
    isCreateBlockedByPendingKyc ||
    isCreateBlockedByBiometryIssue;
  const isEstateActivationComplete = Boolean(activationStatus?.standaloneChargesEnabled);
  const showBackofficeActivationPanel =
    isBrokerAdmin &&
    Boolean(activationEstateId) &&
    activeTab === 'sent' &&
    !activationLoading &&
    activationStatus != null &&
    (isFeatureEnabled
      ? activationStatusError || hasLocalDraft || !isEstateActivationComplete
      : true);

  const showTopKycBanner =
    (activationIsPendingKyc ||
      activationIsBiometryRejected ||
      activationIsSupportRequired) &&
    !showBackofficeActivationPanel;

  const getCreateBlockMessage = () => {
    if (activationIsBiometryRejected) {
      return canRetryBiometry
        ? 'A biometria foi recusada. Tente novamente para liberar a emissão de cobranças.'
        : 'Limite de tentativas de biometria atingido. Entre em contato com o suporte.';
    }
    if (activationIsSupportRequired) {
      return 'Seu cadastro precisa de análise manual. Entre em contato com o suporte.';
    }
    if (isCreateBlockedByPendingKyc) {
      return 'Conclua a biometria da Pagar.me para liberar a emissão de cobranças.';
    }
    if (isCreateBlockedByActivation) {
      return 'Conclua o cadastro de ativação para emitir cobranças.';
    }
    return '';
  };

  const userHasCreatePermission =
    isAdminUser ||
    (isEstateUser && canCreateCharges);
  const userCanSend = isAdminUser || isEstateUser;
  const userCanCancel = isAdminUser || isEstateUser;
  const userCanRenew = userHasCreatePermission;

  const createChargeForm = useCreateStandaloneChargeForm({
    isEstateUser,
    defaultEstateId: appliedFilters.estateId || activationEstateId,
    isCreateBlocked,
    getCreateBlockMessage,
    onCreated: (chargeLinkUrl: string, paymentMethod: 'PIX' | 'BOLETO') => {
      setCreatedChargeLink(chargeLinkUrl);
      setCreatedPaymentMethod(paymentMethod);
      setSuccessModalOpen(true);
      setCurrentPage(1);
      loadData(1, appliedFilters);
    }
  });

  useEffect(() => {
    if (!createChargeForm.createModalOpen) return;
    setTimeout(() => {
      const firstField = document.getElementById('create-amount-input');
      firstField?.focus();
    }, 0);
  }, [createChargeForm.createModalOpen]);

  const openChargeDetails = (chargeId: string) => {
    setDetailsChargeId(chargeId);
    setDetailsModalOpen(true);
  };

  const closeChargeDetails = () => {
    setDetailsModalOpen(false);
    setDetailsChargeId(null);
  };

  const buildListFilters = (sourceFilters = filters): ListStandaloneChargesFiltersDTO => {
    const payerFilter = sourceFilters.type === 'payer' ? sourceFilters.argument : '';
    const documentFilter =
      sourceFilters.type === 'document' ? unmaskCpfOrCnpj(sourceFilters.argument) : '';
    const emailFilter = sourceFilters.type === 'email' ? sourceFilters.argument : '';

    return {
      estateId: sourceFilters.estateId || undefined,
      estateUnitId: sourceFilters.estateUnitId || undefined,
      payer: payerFilter || undefined,
      document: documentFilter || undefined,
      email: emailFilter || undefined,
      status: sourceFilters.status || undefined,
      startDate: sourceFilters.startDate || undefined,
      endDate: sourceFilters.endDate || undefined
    };
  };

  const handleExportReport = async () => {
    setExportLoading(true);

    try {
      await exportStandaloneChargesReport(buildListFilters());
      successNotification('Sucesso', 'Relatório de cobranças exportado.');
    } catch (error) {
      dangerNotification(
        'Erro',
        'Não foi possível exportar o relatório de cobranças.'
      );
    } finally {
      setExportLoading(false);
    }
  };

  const openKycLink = () => {
    if (!activationKycLink) {
      dangerNotification('Atenção', 'Link de biometria indisponível no momento.');
      return;
    }
    window.open(toAbsoluteUrl(activationKycLink), '_blank', 'noopener,noreferrer');
  };

  const activationWizardContent = (
    <ActivationStepContent
      activationStep={activationStep}
      activationForm={activationForm}
      updateActivationFormField={updateActivationFormField}
      handleZipCodeLookup={handleZipCodeLookup}
      addressLookupLoading={addressLookupLoading}
      addressLookupError={addressLookupError}
      addressAutoFilled={addressAutoFilled}
    />
  );

  const activationWizardModal = (
    <ActivationWizardModal
      open={activationWizardOpen}
      onClose={closeActivationWizard}
      activationStep={activationStep}
      lastActivationStep={lastActivationStep}
      activationSaving={activationSaving}
      activationStepError={activationStepError}
      recipientType={activationForm.recipientType}
      onBack={handleBackActivationStep}
      onNext={handleNextActivationStep}
      onFinish={handleActivate}
      finishButtonText={
        activationIsPendingKyc ? 'Salvar alterações' : 'Concluir ativação'
      }
      closeOnDimmerClick={false}
    >
      {activationWizardContent}
    </ActivationWizardModal>
  );

  const sharedActivationGateProps = {
    featureEnabled: isFeatureEnabled,
    activationLoading,
    activationStatusError,
    activationStatus,
    activationIsPendingKyc,
    activationIsBiometryRejected,
    activationIsSupportRequired,
    canRetryBiometry,
    activationKycLink,
    activationKycPageUrl,
    activationKycQrImageUrl,
    openKycLink,
    loadActivationStatus,
    showActivationAction,
    activationActionLabel,
    hasLocalDraft,
    openActivationWizard,
    onRetryBiometry: openRetryBiometryModal
  };

  const retryBiometryModal = (
    <RetryBiometryConfirmModal
      open={retryBiometryModalOpen}
      loading={retryBiometrySaving}
      attemptCount={activationBiometryMeta?.recipientAttemptCount}
      maxAttempts={activationBiometryMeta?.maxRecipientAttempts}
      onConfirm={handleRetryBiometry}
      onClose={closeRetryBiometryModal}
    />
  );

  if (needsActivationGate && needsOnboarding) {
    return (
      <>
        <ActivationGate {...sharedActivationGateProps} />
        {activationWizardModal}
        {retryBiometryModal}
      </>
    );
  }

  return (
    <>
      {showTopKycBanner && (
        <KycBanner
          openKycLink={openKycLink}
          loadActivationStatus={loadActivationStatus}
          activationLoading={activationLoading}
          activationKycPageUrl={activationKycPageUrl}
          onEditActivation={
            activationIsPendingKyc ? openActivationWizard : undefined
          }
          activationIsBiometryRejected={activationIsBiometryRejected}
          activationIsSupportRequired={activationIsSupportRequired}
          canRetryBiometry={canRetryBiometry}
          activationBiometryMeta={activationBiometryMeta}
          onRetryBiometry={openRetryBiometryModal}
        />
      )}

      <ChargesTabs
        activeTab={activeTab}
        showEstatesTab={isAdminUser}
        onChange={setActiveTab}
      />

      {activeTab === 'sent' ? (
        <>
          <FiltersBar
            isEstateUser={isEstateUser}
            estateOptions={estateOptions}
            filters={filters}
            setFilters={setFilters}
            statusOptions={statusOptions}
            onSearch={handleSearch}
            onClear={clearFilters}
            onExport={handleExportReport}
            exportLoading={exportLoading}
          />

          {isBrokerAdmin && !activationEstateId && (
            <Message info style={{ marginBottom: 16 }}>
              Selecione uma imobiliária nos filtros para gerenciar a ativação de Cobranças.
            </Message>
          )}

          {showBackofficeActivationPanel && (
            <ActivationGate compact {...sharedActivationGateProps} />
          )}

          <Segment>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 12
              }}
            >
              <h3 style={{ margin: 0 }}>{standaloneChargesLabel}</h3>
              {userHasCreatePermission && (
                <Button
                  type="button"
                  text="Nova Cobrança"
                  onClick={createChargeForm.openCreateModal}
                  disabled={isCreateBlocked}
                />
              )}
            </div>
            {isCreateBlocked && (
              <Message warning style={{ marginTop: 0 }}>
                <p style={{ margin: 0 }}>{getCreateBlockMessage()}</p>
              </Message>
            )}

            <ChargesTable
              isLoading={isLoading}
              items={items}
              statusLabelMap={statusLabelMap}
              currentPage={currentPage}
              total={total}
              setCurrentPage={setCurrentPage}
              actionLoading={actionLoading}
              userCanSend={userCanSend}
              userCanCancel={userCanCancel}
              userCanRenew={userCanRenew}
              showEstateColumn={!isEstateUser}
              onRowClick={openChargeDetails}
              onCopyLink={copyChargeLink}
              onResendEmail={handleResendEmail}
              onCancel={openCancelModal}
              onRenew={handleRenewCharge}
            />
          </Segment>
        </>
      ) : activeTab === 'commissioning' ? (
        <CommissioningChargesList
          isBrokerAdmin={isBrokerAdmin}
          isEstateAdmin={isEstateAdmin}
          defaultEstateId={activationEstateId || availableBrokersAndEstates?.activeEstateId || user?.estate?.id || ''}
          estateOptions={estateOptions}
          currentUserId={user?.id}
          currentUserDocument={user?.document}
        />
      ) : (
        <EstatesChargesList onOpenEstateInSentTab={openEstateInSentTab} />
      )}

      {activationWizardModal}

      <CreateChargeModal
        open={createChargeForm.createModalOpen}
        onClose={createChargeForm.closeCreateModal}
        isEstateUser={isEstateUser}
        estateOptions={estateOptions}
        defaultEstateId={activationEstateId}
        form={createChargeForm.createForm}
        errors={createChargeForm.createFormErrors}
        saving={createChargeForm.savingCreate}
        onChangeField={createChargeForm.updateCreateFormField}
        onReview={createChargeForm.validateCreateForm}
        onSubmit={createChargeForm.handleCreateCharge}
        addressLookupLoading={createChargeForm.addressLookupLoading}
        addressLookupError={createChargeForm.addressLookupError}
        addressAutoFilled={createChargeForm.addressAutoFilled}
        onPayerAddressZipCodeInput={createChargeForm.onPayerAddressZipCodeInput}
      />

      <SuccessModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        chargeLink={createdChargeLink}
        paymentMethod={createdPaymentMethod}
        onCopy={() => copyChargeLink(createdChargeLink)}
      />

      <CancelChargeModal
        open={cancelModalOpen}
        onClose={closeCancelModal}
        charge={chargeToCancel}
        loading={Boolean(actionLoading?.action === 'cancel')}
        onConfirm={confirmCancelCharge}
      />

      <ChargeDetailsModal
        open={detailsModalOpen}
        chargeId={detailsChargeId}
        statusLabelMap={statusLabelMap}
        hideEstateSection={isEstateUser}
        onClose={closeChargeDetails}
        onCopyLink={copyChargeLink}
      />

      {activationWizardModal}
      {retryBiometryModal}
    </>
  );
};

export default connect(mapState)(StandaloneChargesPage);
